/*
 * tol-product-kouza.js — ワークショップ別商品ページ（product.kouza-workshop）の日時カレンダー / 残席 / 数量制御。
 * ============================================================================================
 * 正本: docs/product-master/workshop-pdp-rebuild-design.md（W2）・DECISIONS 2026-06-30 WS ADR。
 * 移植元: LIVE(Expanse) assets/aw-product-kouza.js + snippets/aw-product-template-kouza.liquid（カレンダー
 *   render/addDay/selectDate・renderStockText・calcDynamicMax・applyAtcReason）。ロジックを再利用し、
 *   DOM/イベント結線を Dawn 契約（<product-info>/<quantity-input>/product-form.js）へ再配線した。
 *
 * ★ 経路A（Dawn 再フェッチを使わない・設計主線）:
 *   Dawn の variant swap 再フェッチ（product-info.js の optionValueSelectionChange）は使わない。
 *   価格は 2MF 固定表示（Liquid・variant 非連動）、残席/数量はこの JS が variant-dates JSON から自前描画。
 *   隠し [name=id] には確定時に variant.id をセット（/cart/add の POST 用途のみ）。
 *   → 最大リスク R1（CTA 上書き衝突）を構造的に発生させない。
 *
 * ★ 本セッション（W2）のスコープ:
 *   - カレンダー描画・日付/時間選択・月送り・過去日/当日ガード・満席判定（LIVE 逐語ロジック）。
 *   - 確定（決定ボタン）→ hidden [name=id] に選択 variant.id をセット + CustomEvent 'kouza:variant-confirmed' を dispatch。
 *     クリア（未選択に戻る）→ [name=id] を空に + 'kouza:variant-cleared' を dispatch。
 *   - 残席描画（Dawn の #Inventory 宿主 = [data-stock-el] に「残り N 名/組」「満席」）。
 *   - 数量制御（Dawn <quantity-input> の input に max 動的設定・満席で disabled・初期は非選択で無効）。
 *
 * ★ 本セッションで「やらない」こと（W5・次セッション・実装前に二系統レビュー必須）:
 *   - 予約 CTA（#ProductSubmitButton）の enable（disabled/aria-disabled を外す）は W5。
 *     本 JS は接続イベントを dispatch するだけで submit ボタンは触らない（初期 disabled 維持 = fail-closed）。
 *   - variantChange 購読の再有効化ガード（product-info.js 再フェッチ後の enable 再適用）も W5。
 *   - applyAtcReason の多理由集約も W5（enable の owner を W5 が持つため）。
 *   - locale キー kouza_reserve は W5 の「予約手続きに進む」文言用に前置き（本 JS では未使用）。
 *
 * fail-closed: variant-dates 空/未初期化・日時未選択・満席・variant 不正のときは confirmed を dispatch せず、
 *   数量を無効・残席を隠す（購入不可のまま）。★「¥0 枠」は workshop 限定（W0 A3）＝R15 T0 以後は有償の講座（kouza・
 *   最大 ¥176,020）も同じ経路に乗る。保護は W5 の二重予約ガード（tol-pdp-form.js cartUnsub）と R12 層 (d)。
 *
 * ★ R15 T0（2026-09-08）direct モード: Default Title 1 件の講座（LIVE の (b) 型）はカレンダーを描かず、
 *   snippets/tol-kouza-direct.liquid が同一キーの variant JSON 1 件と残席 i18n を持つ。initKouza は
 *   root の data-tol-kouza-mode="direct" を見て、isReservable なら confirmed を 1 回 dispatch（owner が enable）、
 *   でなければ dispatch せず数量無効（サーバーレンダの初期 disabled＋「売り切れ」が残る）。
 *   dispatch 前に pdp._tolKouzaDirect を置く（owner 装着順が崩れたときの保険・owner 側 acceptConfirmed が読む）。
 * ★ R15 ⑤: variant JSON の decision_status（「開講中止」= 予約不可・「開講決定」= 日セルの緑ドット＋確定時バッジ）。
 *   判定は decisionOf() 1 箇所（MF は自由入力の single_line_text＝trim して比較・日本語リテラルはここだけ）。
 * ★ 単発講座のドロップダウン（2026-10）: 日付 variant ありでタグ workshop の無い商品は、section の use_dropdown により
 *   カレンダーでなく snippets/tol-kouza-dropdown.liquid（[data-tol-kouza-dd]）が出る。initDropdown がトリガ下のパネルに
 *   日程の行を variant-dates JSON から組み立て、行を選んだ瞬間に確定する。確定/クリア・残席・数量・開講決定バッジはカレンダーと同じ関数
 *   （dispatchConfirmed / dispatchCleared / renderStock / updateQty / renderDecisionBadge）を通す＝owner（tol-pdp-form.js）は無改修。
 *
 * ★ i18n（二系統レビュー MEDIUM-2）: JS は locale を直接読めないため、動的生成する文言（残席/満席/単位/
 *   月名/曜日相当/過去・当日 notice/選択ラベル）は calendar snippet の data-i18n-* 属性（Liquid の | t）から読む。
 */
(function () {
  'use strict';

  var HARD_MAX_QTY = 99;

  function toInt(value, fallback) {
    var n = parseInt(value, 10);
    return isNaN(n) ? fallback : n;
  }

  function fmtDate(d) {
    return (
      d.getFullYear() +
      '-' +
      String(d.getMonth() + 1).padStart(2, '0') +
      '-' +
      String(d.getDate()).padStart(2, '0')
    );
  }

  /* ★ R15 ⑤: 開講判断ステータス（variant MF custom.lecture_decision_status）の正規化。
     'confirmed' = 開講決定 / 'cancelled' = 開講中止 / null = それ以外・未設定。データ値の日本語リテラルはここだけ。 */
  var STATUS_CONFIRMED = '開講決定';
  var STATUS_CANCELLED = '開講中止';
  function decisionOf(v) {
    if (!v || v.decision_status == null) return null;
    var s = String(v.decision_status).trim();
    if (s === STATUS_CONFIRMED) return 'confirmed';
    if (s === STATUS_CANCELLED) return 'cancelled';
    return null;
  }

  /* ★ 在庫管理 variant が「予約可能」か（MEDIUM-1・calcDynamicMax と同一判定を全経路で統一）。
     inventory_policy=continue（在庫切れでも販売継続）は available=true なら qty<=0 でも予約可能。
     この判定を allFull / slot isSoldOut / 残席表示で共通利用する（不整合を排除）。
     ★ R15 ⑤: 「開講中止」は在庫に依らず予約不可（LIVE :869 isCancelled 逐語・addDay/slot/updateQty/dispatchConfirmed の
     4 経路がこの 1 行で揃う）。 */
  function isReservable(v) {
    if (decisionOf(v) === 'cancelled') return false;
    if (!v || !v.available) return false;
    var managed = v.inventory_management === 'shopify';
    var canContinue = v.inventory_policy === 'continue';
    if (!managed || canContinue) return true; // 在庫非管理 or continue は在庫数に依らず可
    return toInt(v.inventory_quantity, 0) > 0;
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /* ★ 単発講座のドロップダウン: option2（開催時間）の並べ替え用キー。"9:00" と "10:00" を文字列比較で
     取り違えないよう HHMM に 0 埋めする（全角コロンも許容）。読めない値はそのまま文字列で比べる。 */
  function timeKey(t) {
    var m = String(t || '').replace('：', ':').match(/(\d{1,2}):(\d{2})/);
    return m ? ('0' + m[1]).slice(-2) + m[2] : String(t || '');
  }

  /* ---- 各 WS PDP（<product-info data-tol-kouza>）を初期化 ---- */
  /* ★「開催店舗を見る」リンク（二系統レビュー critical MEDIUM 是正・dead link を出さない fail-closed）:
     アンカー先（#kzStoreSection = tol-location-detail が WS 埋め込みモードで付与・W7 で結線）が存在しなければ
     （location 未描画で開催店舗セクション非表示）リンクを非表示にし、存在すればスムーススクロールさせる
     （凍結の onclick scrollIntoView 相当・JS で堅牢化）。href 属性の値を読むため JS 側の id 変更は不要。
     ★ 予約可否（tolKouzaEnabled）や variant/カレンダーの有無とは独立に処理する（開催店舗の表示は location 依存で
     予約可否非依存）。initKouza 本体の早期 return（enabled=false / variants 空 / cal 不在）に巻き込まれると
     可視 dead link が残るため、専用関数として initKouza とは別に呼ぶ（W7 で早期 return 前方へ移設）。 */
  function initStoreScrollLink(pdp) {
    if (!pdp || pdp.dataset.tolStoreLinkBound === '1') return;
    var storeScroll = pdp.querySelector('[data-tol-kouza-store-scroll]');
    if (!storeScroll) return;
    pdp.dataset.tolStoreLinkBound = '1';
    var storeTarget = null;
    var href = storeScroll.getAttribute('href') || '';
    if (href.charAt(0) === '#' && href.length > 1) {
      try {
        storeTarget = document.getElementById(href.slice(1));
      } catch (e) {
        storeTarget = null;
      }
    }
    if (!storeTarget) {
      storeScroll.hidden = true; // スクロール先なし＝可視 dead link を出さない
    } else {
      // click は listenerOpts（AbortController signal）を付けない＝意図的（二系統レビュー critical M-2）。
      // このリンクは product-info（pdp）配下に存在し section:load で DOM ごと置換される＝旧ノードとリスナは
      // ブラウザが GC する（storefront は full reload）。modal 系の document listener（下記 listenerOpts）と違い
      // 個別 teardown は不要。signal を足すと未定義（listenerOpts は後方で定義）＝ここでは付けない。
      storeScroll.addEventListener('click', function (e) {
        e.preventDefault();
        storeTarget.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  }

  /* ★ R15 ④: 講座実施日の「全日程を表示／閉じる」トグル（LIVE :1050-1062 逐語・hidden と aria-expanded のトグル）。
     予約可否・variant 有無に依存しないので initStoreScrollLink と同様に initKouza の early return より前で結線する。
     文言は data-label-open/close（Liquid の | t）。JS 不達なら折りたたみのまま＝要約行は見える（fail-safe）。 */
  function initLessondatesToggle(pdp) {
    if (!pdp) return;
    var toggle = pdp.querySelector('[data-tol-kouza-lessondates-toggle]');
    if (!toggle || toggle.dataset.tolBound === '1') return;
    var list = document.getElementById(toggle.getAttribute('aria-controls') || '');
    if (!list) return;
    toggle.dataset.tolBound = '1';
    toggle.addEventListener('click', function () {
      var open = list.hidden;
      list.hidden = !open;
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      toggle.textContent = open ? (toggle.dataset.labelClose || '') : (toggle.dataset.labelOpen || '');
    });
  }

  /* ★ R15 ③: 対象者バッジの「?」説明モーダル（LIVE :1338-1357 の open/close を、カレンダーモーダルと同じ
     フォーカス管理・Escape・scroll-lock 復帰・AbortController teardown で実装）。予約可否に依存しないので early return より前。 */
  function initAudienceModal(pdp) {
    if (!pdp) return;
    var trigger = pdp.querySelector('[data-tol-kouza-audience-trigger]');
    var overlay = pdp.querySelector('[data-tol-kouza-audience-overlay]');
    var modal = pdp.querySelector('[data-tol-kouza-audience-modal]');
    if (!trigger || !overlay || !modal || trigger.dataset.tolBound === '1') return;
    trigger.dataset.tolBound = '1';
    var closeBtn = modal.querySelector('[data-tol-kouza-audience-close]');
    var previousFocus = null;
    var ac = typeof AbortController !== 'undefined' ? new AbortController() : null;
    var opts = ac ? { signal: ac.signal } : false;
    function open() {
      previousFocus = document.activeElement;
      overlay.hidden = false;
      modal.hidden = false;
      trigger.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
      if (closeBtn && typeof closeBtn.focus === 'function') closeBtn.focus();
    }
    function close() {
      overlay.hidden = true;
      modal.hidden = true;
      trigger.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
      if (previousFocus && typeof previousFocus.focus === 'function') previousFocus.focus();
      previousFocus = null;
    }
    trigger.addEventListener('click', open, opts);
    if (closeBtn) closeBtn.addEventListener('click', close, opts);
    overlay.addEventListener('click', close, opts);
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !modal.hidden) close();
    }, opts);
    if (ac) {
      /* ★ once を使わない（Codex correctness lens 2026-09-08）: 別セクションの unload が先に発火すると
         { once: true } の監視がそこで消費され、対象セクションの unload で scroll-lock が解除されず
         Escape リスナーも残る。signal 付きで登録し、対象を含む unload のときだけ復帰＋abort する。 */
      document.addEventListener('shopify:section:unload', function (e) {
        if (e.target && e.target.contains && e.target.contains(pdp)) {
          if (!modal.hidden) document.body.style.overflow = '';
          ac.abort();
        }
      }, opts);
    }
  }

  function initKouza(pdp) {
    initStoreScrollLink(pdp); // 予約可否・variant 有無に依存せず先に結線（fail-closed・early return に巻き込まない）
    initLessondatesToggle(pdp); // R15 ④・同上
    initAudienceModal(pdp); // R15 ③・同上
    if (!pdp || pdp.dataset.tolKouzaBound === '1') return;
    if (pdp.dataset.tolKouzaEnabled !== 'true') return; // reservation_enabled=false は非初期化（fail-closed）
    pdp.dataset.tolKouzaBound = '1';

    var familyFriendly = pdp.dataset.familyFriendly === '1';

    /* 数量 / 残席（早期 return でも参照するので先に解決） */
    var qtyRoot = pdp.querySelector('[data-kouza-qty-root]');
    var stockEl = pdp.querySelector('[data-stock-el]');
    var qtyInput = qtyRoot
      ? document.getElementById(qtyRoot.getAttribute('data-qty-input-id'))
      : null;
    var minQty = qtyRoot ? toInt(qtyRoot.getAttribute('data-min-qty'), 1) : 1;
    var baseMax = qtyRoot
      ? toInt(qtyRoot.getAttribute('data-base-max-qty'), HARD_MAX_QTY)
      : HARD_MAX_QTY;

    /* LOW-1: 数量 UI を無効化する共通処理（早期 return でも呼ぶ＝partial-active を出さない） */
    function disableQty() {
      if (qtyInput) qtyInput.disabled = true;
      if (!qtyRoot) return;
      var mb = qtyRoot.querySelector('.quantity__button[name="minus"]');
      var pb = qtyRoot.querySelector('.quantity__button[name="plus"]');
      /* #99（critical 実 diff MINOR-1）: Dawn の無効の見た目は .disabled クラスだけ（base.css:1341・:disabled の規則は無い） */
      if (mb) {
        mb.disabled = true;
        mb.classList.add('disabled');
      }
      if (pb) {
        pb.disabled = true;
        pb.classList.add('disabled');
      }
    }

    /* ★ ds04 #99（DECISIONS 2026-10-08（9））: 人数の行を戻したので、値の補正を kouza が毎回やり直す。
       Dawn の QuantityInput（global.js）は ± の押下と手入力で値を動かし change を出すだけで、kouza が付けた native disabled も
       上限も見ない。Dawn の product-info.js は cartUpdate のたびに setQuantityBoundries で value を min に戻し max を外して
       quantityUpdate を publish する。そこで
         ・change（± も手入力も）→ updateQty(今の枠) で整数・下限・min(5, quantity_rule.max, 残席) に収め、± の状態も計算し直す。
         ・quantityUpdate → 覚えておいた人数を戻してから updateQty(今の枠)。
       人数は updateQty の最後と input イベントで覚える（Dawn が先に value を書き換えるため・枠を変えて補正された値も覚える）。
       購入ガードで閉じた講座（tolCartGuardBlocked）は人数も有効にしない（CTA の owner が付かない＝送れない）。
       listener は早い return（direct・データ空）の前に付け、自セクションの unload で外す。 */
    var qtyVariant = null; // 今の枠（確定した枠 / direct の variant）。cleared で null
    var lastQty = null;
    var guardBlocked = pdp.dataset.tolCartGuardBlocked === 'true';
    if (qtyInput) {
      var qtyAc = typeof AbortController !== 'undefined' ? new AbortController() : null;
      var qtyOpts = qtyAc ? { signal: qtyAc.signal } : false;
      qtyInput.addEventListener('input', function () {
        var typed = parseInt(qtyInput.value, 10);
        if (typed > 0) lastQty = typed;
      }, qtyOpts);
      qtyInput.addEventListener('change', function () {
        if (qtyVariant) updateQty(qtyVariant);
      }, qtyOpts);
      var unsubQty = null;
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.quantityUpdate) {
        unsubQty = subscribe(PUB_SUB_EVENTS.quantityUpdate, function () {
          if (lastQty != null) qtyInput.value = lastQty;
          if (!qtyVariant) {
            disableQty();
            return;
          }
          updateQty(qtyVariant);
        });
      }
      if (qtyAc) {
        if (typeof unsubQty === 'function') qtyAc.signal.addEventListener('abort', unsubQty);
        document.addEventListener('shopify:section:unload', function (e) {
          if (e.target && e.target.contains && e.target.contains(pdp)) qtyAc.abort();
        }, qtyOpts);
      }
    }

    /* variant-dates JSON を読む */
    var datesEl = pdp.querySelector('[data-tol-kouza-dates]');
    var variants = [];
    if (datesEl) {
      try {
        variants = JSON.parse(datesEl.textContent) || [];
      } catch (e) {
        variants = [];
      }
    }
    if (!variants.length) {
      disableQty(); // LOW-1: データ空でも数量を無効化（submit はサーバーレンダで disabled＝購入不可のまま）
      return; // fail-closed
    }

    /* 日付→variant[] のマップ */
    var eventDates = {};
    variants.forEach(function (v) {
      if (!v.date) return;
      if (!eventDates[v.date]) eventDates[v.date] = [];
      eventDates[v.date].push(v);
    });

    /* カレンダー DOM／direct の器（R15 T0）／単発講座のドロップダウン（2026-10）。
       i18n と form id の供給元は cal・directEl・dd のどれか（どれも無ければ fail-closed）。
       dd は cal と同時には出ない（section の use_dropdown で排他）。 */
    var cal = pdp.querySelector('[data-tol-kouza-cal]');
    var directEl = pdp.querySelector('[data-tol-kouza-direct]');
    var dd = cal ? null : pdp.querySelector('[data-tol-kouza-dd]');
    var isDirect = pdp.dataset.tolKouzaMode === 'direct' && !!directEl && !cal && !dd;
    var i18nSrc = cal || directEl || dd;
    if (!i18nSrc) {
      disableQty();
      return;
    }
    var trigger = cal ? cal.querySelector('[data-tol-kouza-trigger]') : null;
    var triggerLabel = cal ? cal.querySelector('[data-tol-kouza-trigger-label]') : null;
    var selectedBox = cal ? cal.querySelector('[data-tol-kouza-selected]') : null;
    var overlay = cal ? cal.querySelector('[data-tol-kouza-overlay]') : null;
    var modal = cal ? cal.querySelector('[data-tol-kouza-modal]') : null;
    var closeBtn = cal ? cal.querySelector('[data-tol-kouza-close]') : null;
    var prevBtn = cal ? cal.querySelector('[data-tol-kouza-prev]') : null;
    var nextBtn = cal ? cal.querySelector('[data-tol-kouza-next]') : null;
    var titleEl = cal ? cal.querySelector('[data-tol-kouza-title]') : null;
    var grid = cal ? cal.querySelector('[data-tol-kouza-grid]') : null;
    var rightDate = cal ? cal.querySelector('[data-tol-kouza-right-date]') : null;
    var timesEl = cal ? cal.querySelector('[data-tol-kouza-times]') : null;
    var confirmBtn = cal ? cal.querySelector('[data-tol-kouza-confirm]') : null;

    /* i18n（MEDIUM-2・data-i18n-* から取得。direct では tol-kouza-direct の 4 キーだけ＝残りは使われない） */
    var ds = i18nSrc.dataset;
    var i18n = {
      seatsRemaining: ds.i18nSeatsRemaining || '残り__N____U__',
      unitPerson: ds.i18nUnitPerson || '名',
      unitGroup: ds.i18nUnitGroup || '組',
      soldOut: ds.i18nSoldOut || '満席のため追加できません',
      past: ds.i18nPast || 'この日程の開催は終了しております。',
      today: ds.i18nToday || '当日のご予約は直接店舗にお問い合わせください。',
      selectedPrefix: ds.i18nSelectedPrefix || '選択中：',
      changeSuffix: ds.i18nChangeSuffix || ' を変更する',
      months: (ds.i18nMonths || '1月,2月,3月,4月,5月,6月,7月,8月,9月,10月,11月,12月').split(','),
      dateFormat: ds.i18nDateFormat || '{{ year }}年{{ month }}月{{ day }}日',
      slotFull: ds.i18nSlotFull || '満席',
      cancelled: ds.i18nCancelled || '開講中止',
      cancelledNotice: ds.i18nCancelledNotice || 'この日程は開講中止となりました。',
    };
    /* R15 ②: 開講決定バッジ（snippets/tol-kouza-audience.liquid が hidden で事前描画）。確定した variant の decision_status で
       hidden をトグルする（LIVE :935-942 の innerHTML 生成の代替）。再選択・クリアでは隠す（LIVE は stale・意図的差分）。 */
    var decisionBadge = pdp.querySelector('[data-tol-kouza-decision-badge]');
    function renderDecisionBadge(variant) {
      if (!decisionBadge) return;
      decisionBadge.hidden = decisionOf(variant) !== 'confirmed';
    }
    var unitLabel = familyFriendly ? i18n.unitGroup : i18n.unitPerson;

    function seatsText(remaining) {
      // "残り__N____U__" 形式のプレースホルダを置換
      return i18n.seatsRemaining.replace('__N__', remaining).replace('__U__', unitLabel);
    }
    function fmtDateLabel(y, m, d) {
      return i18n.dateFormat.replace('{{ year }}', y).replace('{{ month }}', m).replace('{{ day }}', d);
    }

    /* 予約フォーム（POST 用の hidden [name=id]） */
    var formId = i18nSrc.dataset.productFormId;
    var form = formId ? document.getElementById(formId) : null;
    var idInput = form
      ? form.querySelector('[name="id"]')
      : pdp.querySelector('.product-variant-id');

    /* ★ R15 T0 direct モード（Default Title 1 件・カレンダー無し）: LIVE の (b) 型は ATC が最初から有効。
       isReservable（在庫・available・開講中止を単一源で判定）なら confirmed を 1 回 dispatch して owner に enable させる。
       不可なら dispatch せず数量無効＝サーバーレンダの初期 disabled＋「売り切れ」が残る（fail-closed）。
       初期の dispatchCleared() は呼ばない（[name=id] を空にすると owner の canReserve と cartUnsub の照合が外れ
       二重予約ガードが死ぬ）。renderStock/updateQty/dispatchConfirmed は関数宣言（巻き上げ）＝ここから呼べる。 */
    if (isDirect) {
      var dv = variants[0];
      var directState = { variantId: dv.id, reservable: isReservable(dv) };
      pdp._tolKouzaDirect = directState; // 順序保険（owner が装着時に読む・tol-pdp-form.js bootstrapDirect・消費後に owner が破棄）
      /* once を使わない（別セクションの unload に消費されると自セクションで解除されない・critical MINOR）。
         自セクションの unload を捉えたときだけ null 化して自分を外す。 */
      var directUnload = function (e) {
        if (e.target && e.target.contains && e.target.contains(pdp)) {
          pdp._tolKouzaDirect = null; // 再初期化で stale を読ませない
          document.removeEventListener('shopify:section:unload', directUnload);
        }
      };
      document.addEventListener('shopify:section:unload', directUnload);
      renderStock(dv);
      updateQty(dv);
      if (directState.reservable) {
        dispatchConfirmed(dv);
      } else {
        disableQty();
        /* 予約不可（売り切れ・中止）は cleared を明示 dispatch＝owner が装着済みなら applyCtaState(false) で
           disabled を確定する（サーバーレンダの初期 disabled と同じ状態を JS 側からも保証・fail-closed を二重に）。 */
        dispatchCleared();
      }
      return;
    }

    /* ★ 単発講座のドロップダウン（2026-10・旧 LIVE aw-product-template-kouza の use_dropdown 型＝.kz-dd の移植）:
       トリガを押すと真下にパネルが開き、今日以降の日程を日付・時刻順に月ごとに並べる（行＝日付・曜日・時間・タグ・残席）。
       過去は出さない。当日・満席・開講中止は行を disabled（タグで理由を出す）。過去日・当日の判定はブラウザの日付
       （カレンダーの isPast/isToday と同じ基準）。行を選んだ瞬間に確定してパネルを閉じる（カレンダーの「決定」に当たる操作は無い）。
       確定・残席・数量・開講決定バッジはカレンダーと同じ関数を通す＝予約 CTA の owner（tol-pdp-form.js）は無改修。
       キーボード: ↑↓/Home/End で行の移動・Enter/Space で選択（button の既定）・Escape で閉じてトリガへ戻る・Tab で閉じる。
       ★ 下のカレンダー用の var（today 等）はこの時点で未代入なので使わない（自前で base を作る）。 */
    var DD_FEW_SEATS = 3; // 残席がこれ以下なら残席の文字を強調（旧 LIVE .kz-dd__row--few）
    function initDropdown() {
      var ddTrigger = dd.querySelector('[data-tol-kouza-dd-trigger]');
      var ddMain = dd.querySelector('[data-tol-kouza-dd-main]');
      var ddPanel = dd.querySelector('[data-tol-kouza-dd-panel]');
      var emptyEl = dd.querySelector('[data-tol-kouza-dd-empty]');
      if (!ddTrigger || !ddMain || !ddPanel) {
        disableQty();
        return;
      }
      var weekdays = (ds.i18nWeekdays || '日,月,火,水,木,金,土').split(',');
      var dateFmt = ds.i18nDdDate || '__M__/__D__';
      var todayLabel = ds.i18nDdToday || '当日受付不可';
      var confirmedLabel = ds.i18nDdConfirmed || '開講決定';
      var base = new Date();
      base.setHours(0, 0, 0, 0);
      var baseStr = fmtDate(base);

      var rows = [];
      variants.forEach(function (v) {
        if (!v || !v.date) return;
        var d = new Date(v.date + 'T00:00:00');
        if (isNaN(d.getTime())) return; // 日付として読めない option1 は出さない（fail-closed）
        if (d < base) return; // 過去の日程は出さない
        rows.push({ v: v, d: d });
      });
      rows.sort(function (a, b) {
        if (a.v.date !== b.v.date) return a.v.date < b.v.date ? -1 : 1;
        var ta = timeKey(a.v.time);
        var tb = timeKey(b.v.time);
        return ta < tb ? -1 : ta > tb ? 1 : 0;
      });

      function tagHtml(text, mod) {
        return '<span class="tol-kouza-dd__tag tol-kouza-dd__tag--' + mod + '">' + escapeHtml(text) + '</span>';
      }

      var byId = {};
      var rowEls = [];
      var lastKey = null;
      var rowsWrap = null;
      rows.forEach(function (r) {
        var v = r.v;
        var gKey = r.d.getFullYear() + '-' + r.d.getMonth();
        if (gKey !== lastKey) {
          lastKey = gKey;
          var monthText = r.d.getFullYear() + '年 ' + (i18n.months[r.d.getMonth()] || String(r.d.getMonth() + 1));
          rowsWrap = document.createElement('div');
          rowsWrap.className = 'tol-kouza-dd__group';
          rowsWrap.setAttribute('role', 'group');
          rowsWrap.setAttribute('aria-label', monthText);
          var monthEl = document.createElement('p');
          monthEl.className = 'tol-kouza-dd__month';
          monthEl.setAttribute('aria-hidden', 'true');
          monthEl.textContent = monthText;
          rowsWrap.appendChild(monthEl);
          ddPanel.appendChild(rowsWrap);
        }
        var reservable = isReservable(v);
        var isTodayRow = v.date === baseStr;
        var dec = decisionOf(v);
        var selectable = reservable && !isTodayRow;
        var dow = r.d.getDay();
        var dateText = dateFmt.replace('__M__', r.d.getMonth() + 1).replace('__D__', r.d.getDate());
        var dowText = '（' + (weekdays[dow] || '') + '）';
        var dowMod = dow === 0 ? ' tol-kouza-dd__row-dow--sun' : dow === 6 ? ' tol-kouza-dd__row-dow--sat' : '';
        /* タグの優先順位はカレンダーの枠と同じ（中止 → 満席 → 当日）。選べる枠だけ開講決定・残席を出す。 */
        var tags = '';
        if (dec === 'cancelled') tags = tagHtml(i18n.cancelled, 'cancel');
        else if (!reservable) tags = tagHtml(i18n.slotFull, 'full');
        else if (isTodayRow) tags = tagHtml(todayLabel, 'today');
        else if (dec === 'confirmed') tags = tagHtml(confirmedLabel, 'decided');
        var stockText = '';
        var few = false;
        if (selectable && v.inventory_management === 'shopify' && v.inventory_policy !== 'continue') {
          var remaining = toInt(v.inventory_quantity, 0);
          if (remaining > 0) {
            stockText = seatsText(remaining);
            few = remaining <= DD_FEW_SEATS;
          }
        }
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'tol-kouza-dd__row' + (few ? ' tol-kouza-dd__row--few' : '');
        btn.setAttribute('role', 'option');
        btn.setAttribute('aria-selected', 'false');
        btn.setAttribute('data-variant-id', String(v.id));
        if (!selectable) {
          btn.disabled = true;
          btn.setAttribute('aria-disabled', 'true');
        }
        btn.innerHTML =
          '<span class="tol-kouza-dd__row-date">' + escapeHtml(dateText) +
          '<span class="tol-kouza-dd__row-dow' + dowMod + '">' + escapeHtml(dowText) + '</span></span>' +
          '<span class="tol-kouza-dd__row-time">' + escapeHtml(v.time || '') + '</span>' +
          '<span class="tol-kouza-dd__row-tags">' + tags + '</span>' +
          (stockText ? '<span class="tol-kouza-dd__row-stock">' + escapeHtml(stockText) + '</span>' : '');
        byId[String(v.id)] = { v: v, dateText: dateText, dowText: dowText, stockText: stockText, decided: dec === 'confirmed' };
        rowsWrap.appendChild(btn);
        rowEls.push(btn);
      });

      if (!rows.length) {
        if (emptyEl) emptyEl.hidden = false; // 今日以降の日程が無い＝トリガは disabled のまま（CTA も disabled）
      } else {
        ddTrigger.disabled = false;
      }

      function enabledRows() {
        return rowEls.filter(function (b) { return !b.disabled; });
      }
      function isOpen() {
        return !ddPanel.hidden;
      }
      function openPanel() {
        ddPanel.hidden = false;
        ddTrigger.setAttribute('aria-expanded', 'true');
        var target = ddPanel.querySelector('.tol-kouza-dd__row[aria-selected="true"]') || enabledRows()[0];
        if (target) {
          try {
            target.focus({ preventScroll: true });
          } catch (e) {
            target.focus();
          }
          if (typeof target.scrollIntoView === 'function') target.scrollIntoView({ block: 'nearest' });
        }
      }
      function closePanel(focusTrigger) {
        ddPanel.hidden = true;
        ddTrigger.setAttribute('aria-expanded', 'false');
        if (focusTrigger) ddTrigger.focus();
      }
      function choose(btn) {
        var info = byId[btn.getAttribute('data-variant-id')];
        if (!info || !isReservable(info.v) || info.v.date === baseStr) return; // 念のため（disabled 行は click が来ない）
        rowEls.forEach(function (b) {
          b.setAttribute('aria-selected', b === btn ? 'true' : 'false');
        });
        ddMain.innerHTML =
          '<span class="tol-kouza-dd__picked">' +
          '<span>' + escapeHtml(info.dateText) + '<small>' + escapeHtml(info.dowText) + '</small></span>' +
          '<span>' + escapeHtml(info.v.time || '') + '</span>' +
          (info.decided ? tagHtml(confirmedLabel, 'decided') : '') +
          (info.stockText ? '<small>' + escapeHtml(info.stockText) + '</small>' : '') +
          '</span>';
        ddTrigger.classList.add('is-selected');
        renderStock(info.v);
        updateQty(info.v);
        renderDecisionBadge(info.v); // R15 ②
        dispatchConfirmed(info.v); // W5 が購読して予約 CTA を enable
      }

      var ddAc = typeof AbortController !== 'undefined' ? new AbortController() : null;
      var ddOpts = ddAc ? { signal: ddAc.signal } : false;
      ddTrigger.addEventListener('click', function () {
        if (isOpen()) closePanel(false);
        else openPanel();
      }, ddOpts);
      ddTrigger.addEventListener('keydown', function (e) {
        if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && !isOpen()) {
          e.preventDefault();
          openPanel();
        }
      }, ddOpts);
      ddPanel.addEventListener('click', function (e) {
        var btn = e.target && e.target.closest ? e.target.closest('.tol-kouza-dd__row') : null;
        if (!btn || btn.disabled) return;
        choose(btn);
        closePanel(true);
      }, ddOpts);
      ddPanel.addEventListener('keydown', function (e) {
        var list = enabledRows();
        var idx = list.indexOf(document.activeElement);
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          e.preventDefault();
          if (!list.length) return;
          var next = e.key === 'ArrowDown' ? Math.min(idx + 1, list.length - 1) : Math.max(idx - 1, 0);
          if (idx < 0) next = 0;
          list[next].focus();
        } else if (e.key === 'Home' || e.key === 'End') {
          e.preventDefault();
          if (list.length) list[e.key === 'Home' ? 0 : list.length - 1].focus();
        } else if (e.key === 'Escape') {
          e.preventDefault();
          closePanel(true);
        } else if (e.key === 'Tab') {
          closePanel(false);
        }
      }, ddOpts);
      /* パネルの外を押したら閉じる */
      document.addEventListener('click', function (e) {
        if (isOpen() && !dd.contains(e.target)) closePanel(false);
      }, ddOpts);
      if (ddAc) {
        /* once を使わない（別セクションの unload に消費されないよう・initAudienceModal と同じ理由） */
        document.addEventListener('shopify:section:unload', function (e) {
          if (e.target && e.target.contains && e.target.contains(pdp)) ddAc.abort();
        }, ddOpts);
      }

      /* 初期状態: 未選択（カレンダーと同じ） */
      renderStock(null);
      disableQty();
      dispatchCleared();
    }
    if (dd) {
      initDropdown();
      return;
    }

    if (!cal) {
      disableQty();
      return; // calendar モードなのに DOM が無い＝fail-closed
    }

    var curYear, curMonth, selDate = null, selVariant = null;
    var previousFocus = null;
    var today = new Date();
    today.setHours(0, 0, 0, 0);
    var todayStr = fmtDate(today);

    /* teardown 用（MEDIUM-3・AbortController で document listener を再入安全に） */
    var ac = typeof AbortController !== 'undefined' ? new AbortController() : null;
    var listenerOpts = ac ? { signal: ac.signal } : false;

    /* 初期表示月 = 最初の開催日の月 */
    var firstDate = Object.keys(eventDates).sort()[0];
    if (firstDate) {
      var fd = new Date(firstDate + 'T00:00:00');
      curYear = fd.getFullYear();
      curMonth = fd.getMonth();
    } else {
      curYear = today.getFullYear();
      curMonth = today.getMonth();
    }

    function isPast(dateStr) {
      return new Date(dateStr + 'T00:00:00') < today;
    }
    function isToday(dateStr) {
      return dateStr === todayStr;
    }

    /* ---- 残席・数量（LIVE renderStockText / calcDynamicMax / updateQtyButtonUI 相当・isReservable で統一）---- */
    function renderStock(variant) {
      if (!stockEl) return;
      if (!variant) {
        stockEl.style.visibility = 'hidden';
        stockEl.innerHTML = '';
        return;
      }
      /* ★ R15 ⑤: 開講中止は在庫に依らず予約不可＝残席（「残り N 名」）を出さない。
         calendar 経路では中止枠が選択できないのでここへ来ないが、direct 経路は 1 件を無条件に描くため必要。 */
      if (decisionOf(variant) === 'cancelled') {
        stockEl.style.visibility = 'visible';
        stockEl.innerHTML = '<span class="tol-kouza-stock__soldout">' + escapeHtml(i18n.cancelled) + '</span>';
        stockEl.classList.add('tol-kouza-stock--soldout');
        return;
      }
      var managed = variant.inventory_management === 'shopify';
      var canContinue = variant.inventory_policy === 'continue';
      stockEl.style.visibility = 'visible';
      if (!managed || canContinue) {
        stockEl.innerHTML = ''; // 在庫非管理/continue は残席数を出さない（在庫充足扱い）
        stockEl.classList.remove('tol-kouza-stock--soldout');
        return;
      }
      var remaining = Math.max(0, toInt(variant.inventory_quantity, 0));
      if (remaining > 0) {
        stockEl.innerHTML =
          '<span class="tol-kouza-stock__dot" aria-hidden="true"></span>' + escapeHtml(seatsText(remaining));
        stockEl.classList.remove('tol-kouza-stock--soldout');
      } else {
        stockEl.innerHTML = '<span class="tol-kouza-stock__soldout">' + escapeHtml(i18n.soldOut) + '</span>';
        stockEl.classList.add('tol-kouza-stock--soldout');
      }
    }

    function calcDynamicMax(variant) {
      var maxQty = baseMax;
      if (!variant) return maxQty;
      var managed = variant.inventory_management === 'shopify';
      var canContinue = variant.inventory_policy === 'continue';
      if (managed && !canContinue) {
        maxQty = Math.min(maxQty, Math.max(0, toInt(variant.inventory_quantity, 0)));
      }
      return maxQty;
    }

    function updateQty(variant) {
      if (!qtyInput) return;
      qtyVariant = variant || null;
      var reservable = isReservable(variant) && !guardBlocked; // MEDIUM-1: 統一判定（#99: 購入ガード中は有効にしない）
      var maxQty = calcDynamicMax(variant);
      var isSoldOut = !reservable;
      var cur = toInt(qtyInput.value, minQty);
      if (cur < minQty) cur = minQty;
      if (maxQty > 0 && cur > maxQty) cur = maxQty;
      qtyInput.value = cur;
      lastQty = cur; // #99: 補正後の人数を覚える（Dawn の setQuantityBoundries が value を書き換える前に）
      qtyInput.disabled = isSoldOut;
      if (maxQty != null && maxQty !== '') {
        qtyInput.setAttribute('max', String(maxQty));
      }
      /* #99: native disabled と Dawn の .disabled クラス（validateQtyRules が付ける見た目）をそろえる */
      var minusBtn = qtyRoot.querySelector('.quantity__button[name="minus"]');
      var plusBtn = qtyRoot.querySelector('.quantity__button[name="plus"]');
      if (minusBtn) {
        minusBtn.disabled = isSoldOut || cur <= minQty;
        minusBtn.classList.toggle('disabled', minusBtn.disabled);
      }
      if (plusBtn) {
        plusBtn.disabled = isSoldOut || cur >= maxQty;
        plusBtn.classList.toggle('disabled', plusBtn.disabled);
      }
    }

    /* ---- 予約 CTA への接続（W2 は dispatch のみ・enable owner は W5 = tol-pdp-form.js initReservationCta）---- */
    function dispatchConfirmed(variant) {
      if (idInput) {
        idInput.value = variant.id;
        idInput.disabled = false; // POST に含める（product-form.js constructor も解除する）
      }
      /* ★ W5・二系統レビュー HIGH-2 の単一源化: 予約可否の権威判定は kouza の isReservable（continue policy 対応）で
         行い、その結果を detail.reservable に載せる。owner（tol-pdp-form.js）は isReservable を再導出せず
         この boolean を信じる（2ファイルに複製すると片方修正で drift＝満席 ¥0 枠が予約可能になる fail-open を排除）。 */
      pdp.dispatchEvent(
        new CustomEvent('kouza:variant-confirmed', {
          bubbles: true,
          detail: { variantId: variant.id, variant: variant, reservable: isReservable(variant) },
        })
      );
    }
    function dispatchCleared() {
      if (idInput) idInput.value = '';
      /* #99: 枠が外れたら人数も無効に戻す（日程の選び直し中に人数を変えられない・値は覚えたまま） */
      qtyVariant = null;
      disableQty();
      pdp.dispatchEvent(new CustomEvent('kouza:variant-cleared', { bubbles: true }));
    }

    /* ---- カレンダー描画（LIVE render/addDay 相当）---- */
    function render() {
      if (titleEl) titleEl.textContent = curYear + '年 ' + (i18n.months[curMonth] || String(curMonth + 1));
      if (!grid) return;
      grid.innerHTML = '';
      var first = new Date(curYear, curMonth, 1);
      var last = new Date(curYear, curMonth + 1, 0);
      var startDow = first.getDay();
      for (var i = 0; i < startDow; i++) {
        var pad = document.createElement('div');
        pad.className = 'tol-kouza-cal__day tol-kouza-cal__day--pad';
        pad.setAttribute('aria-hidden', 'true');
        grid.appendChild(pad);
      }
      for (var day = 1; day <= last.getDate(); day++) {
        addDay(new Date(curYear, curMonth, day));
      }
    }

    function addDay(d) {
      var key = fmtDate(d);
      var el = document.createElement('div');
      el.className = 'tol-kouza-cal__day';
      el.textContent = d.getDate();
      var hasEvent = !!eventDates[key];
      if (hasEvent) {
        // MEDIUM-1: isReservable で「予約可能な枠が1つでもあるか」を判定（continue policy 対応）
        var anyReservable = eventDates[key].some(isReservable);
        /* R15 ⑤: 開講決定の枠がある日は緑ドット（LIVE :808-809・過去日にも付く）。全枠が開講中止の未来日は
           クリック可にして中止 notice へ到達させる（LIVE :857-863 の現行踏襲・--cancelled で意匠上も区別）。 */
        var anyConfirmed = eventDates[key].some(function (v) { return decisionOf(v) === 'confirmed'; });
        var allCancelled = eventDates[key].every(function (v) { return decisionOf(v) === 'cancelled'; });
        if (anyConfirmed) el.classList.add('tol-kouza-cal__day--confirmed');
        if (isPast(key)) {
          el.classList.add('tol-kouza-cal__day--past');
        } else if (!anyReservable && !allCancelled) {
          el.classList.add('tol-kouza-cal__day--event', 'tol-kouza-cal__day--full');
        } else {
          el.classList.add('tol-kouza-cal__day--event');
          if (allCancelled) el.classList.add('tol-kouza-cal__day--cancelled');
          el.setAttribute('role', 'button');
          el.setAttribute('tabindex', '0');
          el.addEventListener('click', function () {
            selectDate(key);
          });
          el.addEventListener('keydown', function (e) {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              selectDate(key);
            }
          });
        }
        if (key === selDate) el.classList.add('tol-kouza-cal__day--selected');
      }
      grid.appendChild(el);
    }

    function selectDate(dateStr) {
      selDate = dateStr;
      selVariant = null;
      if (confirmBtn) confirmBtn.disabled = true;
      /* ★ W5・二系統レビュー BLOCKER-1（スタレスロット残存の正確性バグ）是正:
         確定後に別日付を選び直すと selVariant=null になるが、確定時にセットした hidden [name=id] と予約 CTA の
         enable 状態が残ったままだと、モーダルを閉じて「予約手続きに進む」を押すと前スロットの在庫を誤確保する（WS は ¥0 でも座席枠の実損・★R15 T0 以後の講座は有償）。
         日付を選び直した瞬間に cleared を投げ、[name=id] を空にして owner（tol-pdp-form.js）に CTA を再 disable させる。
         idInput.value 非空（＝確定済みからの選び直し）のときだけ呼ぶ（未確定の初回選択では冗長を避ける）。 */
      if (idInput && idInput.value) dispatchCleared();
      renderDecisionBadge(null); // R15 ②: 選び直しで stale バッジを残さない
      render();
      var p = dateStr.split('-');
      if (rightDate) {
        rightDate.textContent = fmtDateLabel(p[0], parseInt(p[1], 10), parseInt(p[2], 10));
      }
      if (!timesEl) return;
      timesEl.innerHTML = '';

      if (isPast(dateStr)) {
        appendNotice(i18n.past);
        return;
      }
      if (isToday(dateStr)) {
        appendNotice(i18n.today);
      }
      /* R15 ⑤: その日の全枠が開講中止なら notice（LIVE :857-863 逐語）。枠自体は下で disabled＋「開講中止」ラベル。 */
      if (eventDates[dateStr].every(function (v) { return decisionOf(v) === 'cancelled'; })) {
        appendNotice(i18n.cancelledNotice, 'tol-kouza-cal__notice--cancelled');
      }

      var wrap = document.createElement('div');
      wrap.className = 'tol-kouza-cal__slots';
      eventDates[dateStr].forEach(function (v) {
        var btn = document.createElement('button');
        btn.type = 'button';
        var reservable = isReservable(v); // MEDIUM-1: 統一判定
        var isTodaySlot = isToday(dateStr);
        var disabled = !reservable || isTodaySlot;
        var managed = v.inventory_management === 'shopify';
        var canContinue = v.inventory_policy === 'continue';
        var remaining = toInt(v.inventory_quantity, 0);
        var stockText = '';
        /* R15 ⑤: 中止 → 「開講中止」・満席 → 「満席」（既存の直書きを i18n へ・LIVE :868-873 の順 = 中止が先） */
        var unavailableLabel = decisionOf(v) === 'cancelled' ? i18n.cancelled : i18n.slotFull;
        if (!reservable) {
          stockText = unavailableLabel;
        } else if (managed && !canContinue && remaining > 0) {
          stockText = seatsText(remaining);
        }
        btn.className = 'tol-kouza-cal__slot' + (disabled ? ' tol-kouza-cal__slot--full' : '');
        btn.disabled = disabled;
        btn.innerHTML =
          '<span class="tol-kouza-cal__slot-time">' +
          escapeHtml(v.time || '') +
          (!reservable ? '（' + escapeHtml(unavailableLabel) + '）' : '') +
          '</span>' +
          (stockText ? '<span class="tol-kouza-cal__slot-stock">' + escapeHtml(stockText) + '</span>' : '');
        if (!disabled) {
          btn.addEventListener('click', function () {
            wrap.querySelectorAll('.tol-kouza-cal__slot').forEach(function (b) {
              b.classList.remove('tol-kouza-cal__slot--selected');
            });
            btn.classList.add('tol-kouza-cal__slot--selected');
            selVariant = v;
            if (confirmBtn) confirmBtn.disabled = false;
          });
        }
        wrap.appendChild(btn);
      });
      timesEl.appendChild(wrap);
    }

    function appendNotice(text, extraClass) {
      var notice = document.createElement('p');
      notice.className = 'tol-kouza-cal__notice' + (extraClass ? ' ' + extraClass : '');
      notice.textContent = text;
      timesEl.appendChild(notice);
    }

    /* ---- モーダル開閉（MEDIUM-3・フォーカス管理 + scroll-lock cleanup）---- */
    function openModal() {
      previousFocus = document.activeElement;
      if (overlay) overlay.hidden = false;
      if (modal) modal.hidden = false;
      if (trigger) trigger.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
      render();
      if (closeBtn && typeof closeBtn.focus === 'function') closeBtn.focus();
    }
    function closeModal() {
      if (overlay) overlay.hidden = true;
      if (modal) modal.hidden = true;
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
      if (previousFocus && typeof previousFocus.focus === 'function') previousFocus.focus();
      previousFocus = null;
    }

    if (trigger) trigger.addEventListener('click', openModal, listenerOpts);
    if (closeBtn) closeBtn.addEventListener('click', closeModal, listenerOpts);
    if (overlay) overlay.addEventListener('click', closeModal, listenerOpts);
    if (prevBtn)
      prevBtn.addEventListener('click', function () {
        curMonth--;
        if (curMonth < 0) {
          curMonth = 11;
          curYear--;
        }
        render();
      }, listenerOpts);
    if (nextBtn)
      nextBtn.addEventListener('click', function () {
        curMonth++;
        if (curMonth > 11) {
          curMonth = 0;
          curYear++;
        }
        render();
      }, listenerOpts);
    // MEDIUM-3: document keydown は AbortController の signal 付き（section:unload で abort＝蓄積しない）
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && modal && !modal.hidden) closeModal();
    }, listenerOpts);

    /* ---- 確定 ---- */
    if (confirmBtn)
      confirmBtn.addEventListener('click', function () {
        if (!selVariant) return;
        var p = selDate.split('-');
        var dateLabel = fmtDateLabel(p[0], parseInt(p[1], 10), parseInt(p[2], 10));
        var label = dateLabel + ' ' + (selVariant.time || '');
        if (triggerLabel) {
          triggerLabel.textContent = label + i18n.changeSuffix;
          triggerLabel.classList.add('is-selected');
        }
        if (selectedBox) {
          selectedBox.hidden = false;
          selectedBox.textContent = i18n.selectedPrefix + label;
        }
        renderStock(selVariant);
        updateQty(selVariant);
        renderDecisionBadge(selVariant); // R15 ②
        dispatchConfirmed(selVariant); // W5 が購読して予約 CTA を enable
        closeModal();
      }, listenerOpts);

    /* MEDIUM-3: section:unload で teardown（listener abort + scroll-lock 復帰） */
    if (ac) {
      document.addEventListener(
        'shopify:section:unload',
        function (e) {
          if (e.target && e.target.contains && e.target.contains(pdp)) {
            document.body.style.overflow = '';
            ac.abort();
          }
        },
        { once: true }
      );
    }

    /* 初期状態: 未選択 = 残席 hidden・数量無効・cleared 通知（submit は初期 disabled のまま = fail-closed） */
    renderStock(null);
    disableQty();
    dispatchCleared();
  }

  function initAll(root) {
    (root || document).querySelectorAll('product-info[data-tol-kouza]').forEach(initKouza);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }
  // theme editor での section 再読み込み（bound 済みは initKouza 冒頭でスキップ・新要素のみ初期化）
  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });
})();